import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { ArrowRightLeft, Power, Truck, UserRound, X } from "lucide-react";
import usePlanningWriteActivity from "../hooks/usePlanningWriteActivity.js";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import { useToast } from "./ResultsPrimitives.jsx";
import { DriverEmployeeSelect } from "./DriverEmployeeSelect.jsx";
import { TractorSelect } from "./TractorSelect.jsx";
import {
  deleteStandaloneUnit, freeTractorOptions, saveStandaloneUnitDriver, waitingTrailerOptions,
} from "../lib/tractorAssignmentsApi.js";
import { refreshDispatchDay } from "../utils/dispatchDayRefresh.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";

// Même puce que « Camion à choisir » sur une ligne du plan ; « Remorque à choisir » en neutre.
const WAITING_CHIP_CLASS_NAME = {
  remorque: "border-amber-300 bg-amber-50 text-amber-800",
  camion: "border-slate-300 bg-slate-100 text-slate-700",
};
const PRIMARY_BUTTON_CLASS_NAME = "h-7 rounded-[4px] border border-accent bg-accent px-2 text-xs font-semibold text-white hover:border-accent-strong hover:bg-accent-strong disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";
const EDITOR_CLASS_NAME = "grid gap-2 rounded-[4px] border border-line-soft bg-white p-2";

function optionLabel(option) {
  return { ...option, name: option.kind ? `${option.name} · ${option.kind}` : option.name, details: undefined };
}

function MenuRow({ icon: Icon, label, meta = "", disabled = false, title = "", tone = "default", onClick }) {
  return <button type="button" disabled={disabled} title={title || undefined} onClick={onClick}
    className={[
      "flex w-full min-w-0 items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-[0.8rem] font-semibold transition disabled:cursor-not-allowed",
      tone === "danger"
        ? "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
        : "text-content-ink hover:bg-slate-50 disabled:text-slate-400",
    ].join(" ")}>
    <Icon aria-hidden="true" className="h-4 w-4 shrink-0" strokeWidth={2} />
    <span className="min-w-0 flex-1 truncate">{label}</span>
    {meta ? <span className="shrink-0 text-xs font-normal text-content-muted">{meta}</span> : null}
  </button>;
}

// Attelage depuis une ligne vide, par le geste existant « Changer de camion » ; puis la seule remorque est actualisée.
function CouplingEditor({ ariaLabel, options, placeholder, submitLabel, disabled, onSubmit }) {
  const [selected, setSelected] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit(options.find((option) => option.id === selected));
      setSelected("");
    } finally {
      setBusy(false);
    }
  };
  return <div className={EDITOR_CLASS_NAME} data-standalone-coupling-editor>
    <TractorSelect catalog={{}} options={options.map(optionLabel)} value={selected} ariaLabel={ariaLabel} placeholder={placeholder}
      disabled={busy || disabled} onChange={(id) => setSelected(id || "")} />
    {!options.length ? <p className="m-0 text-xs text-content-muted">Aucun choix pour cette journée.</p> : null}
    <div className="flex items-center gap-2">
      <button type="button" className={PRIMARY_BUTTON_CLASS_NAME} disabled={busy || disabled || !selected} onClick={() => void submit()}>
        {busy ? "Enregistrement…" : submitLabel}
      </button>
    </div>
  </div>;
}

function DriverEditor({ unit, dispatchDate, dispatchPeriod, disabled, onSaved }) {
  const { showToast } = useToast();
  const [selection, setSelection] = React.useState(undefined);
  const [busy, setBusy] = React.useState(false);
  const currentId = unit.employee_id || "";
  const selectedId = selection === undefined ? currentId : selection || "";
  const submit = async () => {
    setBusy(true);
    try {
      const result = await saveStandaloneUnitDriver(unit.id, { employeeId: selectedId || null, expectedVersion: unit.version });
      showToast({ type: "success", title: "Chauffeur enregistré", message: `${unit.label} — ${result?.unit?.driver_name || "Aucun chauffeur"}` });
      setSelection(undefined);
      onSaved?.();
    } catch (failure) {
      showToast({ type: "warning", title: "Chauffeur non enregistré", message: failure.message });
    } finally {
      setBusy(false);
    }
  };
  return <div className={EDITOR_CLASS_NAME} data-standalone-driver-editor>
    <DriverEmployeeSelect value={selectedId} currentName={selection === undefined && !currentId ? unit.driver_name || "" : ""}
      truckId={unit.truck_id} dispatchDate={dispatchDate} dispatchPeriod={dispatchPeriod} disabled={busy || disabled}
      onChange={(employeeId) => setSelection(employeeId || "")} />
    <div className="flex items-center gap-2">
      <button type="button" className={PRIMARY_BUTTON_CLASS_NAME} disabled={busy || disabled || selection === undefined || selectedId === currentId}
        onClick={() => void submit()}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
    </div>
  </div>;
}

function StandaloneUnitMenu({ unit, catalog, anchorElement, dispatchDate, dispatchPeriod, canWrite, onUpdateTruckTractor, onClose }) {
  const { showToast } = useToast();
  const planningWriteBlocked = Boolean(usePlanningWriteActivity());
  const [editor, setEditor] = React.useState("");
  const [isRemoving, setIsRemoving] = React.useState(false);
  const isTrailer = unit.part === "remorque";
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: true, strategy: "fixed", placement: "bottom-start", elements: { reference: anchorElement },
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })], whileElementsMounted: autoUpdate,
  });
  // Les listes des sélecteurs s'ouvrent dans le portail : un clic dedans ne ferme pas le menu.
  usePopoverDismiss({ anchorElement, floatingRef: refs.floating, isOpen: true, onClose,
    shouldIgnoreTarget: (target) => Boolean(target?.closest?.('[role="listbox"]')) });
  const writeDisabled = !canWrite || planningWriteBlocked;
  const writeTitle = canWrite ? "" : "Votre rôle n'a pas ce droit.";
  const couple = async ({ trailerId, trailerLabel, tractorId, expectedVersion }) => {
    let result = null;
    try {
      result = await onUpdateTruckTractor?.({ truckId: trailerId, tractorTruckId: tractorId, expectedVersion, dispatchPeriod, truckName: trailerLabel });
    } catch {
      return; // Le refus est déjà affiché par le geste « Changer de camion ».
    }
    if (!result) return;
    window.dispatchEvent(new CustomEvent("grain-driver-assignments-changed", { detail: { scope: "dated", truckIds: result.modified_truck_ids || [] } }));
    onClose();
    try {
      const summary = await refreshDispatchDay(dispatchDate, { selectTrucks: async (period) => (period === dispatchPeriod
        ? (detail) => detail.truck_id === trailerId : null) });
      if (summary.toReview) {
        showToast({ type: "warning", title: "Attelage enregistré", message: `${trailerLabel} : la carte reste dans Mise à jour.` });
      } else if (!summary.refreshed) {
        showToast({ type: "info", title: "Attelage enregistré", message: "La ligne apparaîtra au prochain Optimiser." });
      }
    } catch (failure) {
      showToast({ type: "warning", title: "Attelage enregistré", message: `${failure.message} La carte reste dans Mise à jour.` });
    }
  };
  const lineOf = (truckId) => (catalog?.lines || []).find((line) => line.truck_id === truckId) || null;
  const remove = async () => {
    setIsRemoving(true);
    try {
      const result = await deleteStandaloneUnit(unit.id);
      showToast({ type: "success", title: "Ligne retirée du Gantt", message: result?.message || unit.label });
      onClose();
    } catch (failure) {
      showToast({ type: "warning", title: "Ligne non retirée", message: failure.message });
      setIsRemoving(false);
    }
  };
  const portalRoot = getReactPortalRoot();
  if (!portalRoot) return null;
  return createPortal(<div ref={refs.setFloating} role="dialog" aria-label={`${unit.label} — ${unit.waiting_label}`} data-standalone-unit-menu={unit.part}
    className="grid w-[21rem] max-w-[calc(100vw-1rem)] gap-1.5 rounded-[4px] border border-line-soft bg-white p-2 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
    style={{ ...floatingStyles, visibility: isPositioned ? "visible" : "hidden", zIndex: 2100 }}>
    <div className="flex items-center gap-2 border-b border-line-soft pb-1.5">
      <h3 className="m-0 min-w-0 flex-1 truncate text-sm font-semibold text-content-ink">{unit.label} — {unit.waiting_label}</h3>
      <button type="button" aria-label="Fermer" className="inline-flex h-7 w-7 items-center justify-center rounded-[4px] text-content-muted hover:bg-slate-50" onClick={onClose}>
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
    <p className="m-0 px-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] text-slate-500">Affectation</p>
    {isTrailer ? <>
      <MenuRow icon={UserRound} label="Modifier chauffeur" meta="—" disabled title="Choisissez d'abord le camion de cette remorque." />
      <p className="m-0 px-2 text-xs font-medium text-amber-800">Choisissez d'abord le camion de cette remorque.</p>
      <MenuRow icon={Truck} label="Changer de camion" meta={unit.waiting_label} disabled={writeDisabled || !onUpdateTruckTractor} title={writeTitle}
        onClick={() => setEditor((current) => (current === "tractor" ? "" : "tractor"))} />
      {editor === "tractor" ? <CouplingEditor ariaLabel="Camion" placeholder="Choisir un camion libre" submitLabel="Atteler"
        options={freeTractorOptions(catalog)} disabled={writeDisabled}
        onSubmit={(option) => couple({ trailerId: unit.truck_id, trailerLabel: unit.label, tractorId: option.id,
          expectedVersion: lineOf(unit.truck_id)?.version })} /> : null}
    </> : <>
      <MenuRow icon={UserRound} label="Modifier chauffeur" meta={unit.driver_name || ""} disabled={writeDisabled} title={writeTitle}
        onClick={() => setEditor((current) => (current === "driver" ? "" : "driver"))} />
      {editor === "driver" ? <DriverEditor unit={unit} dispatchDate={dispatchDate} dispatchPeriod={dispatchPeriod} disabled={writeDisabled}
        onSaved={() => setEditor("")} /> : null}
      <MenuRow icon={ArrowRightLeft} label="Choisir la remorque" meta="à choisir" disabled={writeDisabled || !onUpdateTruckTractor} title={writeTitle}
        onClick={() => setEditor((current) => (current === "trailer" ? "" : "trailer"))} />
      {editor === "trailer" ? <CouplingEditor ariaLabel="Remorque" placeholder="Choisir une remorque sans camion" submitLabel="Atteler"
        options={waitingTrailerOptions(catalog)} disabled={writeDisabled}
        onSubmit={(option) => couple({ trailerId: option.id, trailerLabel: option.name, tractorId: unit.truck_id,
          expectedVersion: option.version })} /> : null}
    </>}
    <MenuRow icon={Power} label={isRemoving ? "Retrait…" : "Retirer du Gantt"} tone="danger" disabled={!canWrite || isRemoving} title={writeTitle}
      onClick={() => void remove()} />
  </div>, portalRoot);
}

function StandaloneUnitRow({ unit, catalog, dispatchDate, dispatchPeriod, canWrite, onUpdateTruckTractor }) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [anchorElement, setAnchorElement] = React.useState(null);
  const isTrailer = unit.part === "remorque";
  return <article data-standalone-unit-row={unit.part} data-truck-id={unit.truck_id}
    className="relative grid gap-1 rounded-[4px] border border-[rgba(148,163,184,0.16)] bg-white px-1 py-0">
    <div className="grid gap-px min-[1120px]:items-center min-[1120px]:grid-cols-[155px_minmax(0,1fr)]">
      <button ref={setAnchorElement} type="button" aria-haspopup="dialog" aria-expanded={isMenuOpen ? "true" : "false"}
        data-standalone-unit-identity="true" onClick={() => setIsMenuOpen((current) => !current)}
        className="grid w-full min-w-0 gap-0.5 rounded-[4px] py-0.5 pl-0 pr-0.5 text-left transition hover:bg-[rgba(248,250,252,0.86)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2">
        <span className="m-0 min-w-0 truncate text-[0.78rem] font-bold leading-none tracking-[-0.02em] text-text">{unit.label}</span>
        <span className="min-w-0 break-words text-[0.68rem] font-medium leading-tight text-content-copy">
          <span className={`mr-1 inline-flex h-4 items-center whitespace-nowrap rounded-[4px] border px-1 align-middle text-[0.58rem] font-bold leading-none ${WAITING_CHIP_CLASS_NAME[unit.part]}`}>
            {unit.waiting_label}
          </span>
          {isTrailer ? null : unit.driver_name || "Chauffeur non renseigné"}
        </span>
      </button>
      <div className="flex h-9 min-w-0 items-center justify-center rounded-[4px] border border-dashed border-[rgba(148,163,184,0.3)] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.94))] px-2 text-center text-[0.64rem] font-medium text-content-copy">
        <span className="min-w-0 truncate">{isTrailer
          ? "Ligne vide de la journée · choisissez son camion dans le menu de la ligne (« Changer de camion »)."
          : "Ligne vide de la journée · choisissez sa remorque dans le menu de la ligne (« Choisir la remorque »). Aucune commande."}</span>
      </div>
    </div>
    {isMenuOpen && anchorElement ? <StandaloneUnitMenu unit={unit} catalog={catalog} anchorElement={anchorElement} dispatchDate={dispatchDate}
      dispatchPeriod={dispatchPeriod} canWrite={canWrite} onUpdateTruckTractor={onUpdateTruckTractor} onClose={() => setIsMenuOpen(false)} /> : null}
  </article>;
}

// Lignes vides de la journée, à la fin des lignes du Gantt : une remorque déjà au plan n'est pas répétée.
export default function GanttStandaloneUnitRows({ catalog = null, ganttTruckIds = new Set(), dispatchDate = "", dispatchPeriod = "day",
  canWrite = false, onUpdateTruckTractor = null }) {
  const units = (catalog?.standalone_units || []).filter((unit) => unit.part === "camion" || !ganttTruckIds.has(unit.truck_id));
  return units.map((unit) => <StandaloneUnitRow key={unit.id} unit={unit} catalog={catalog} dispatchDate={dispatchDate}
    dispatchPeriod={dispatchPeriod} canWrite={canWrite} onUpdateTruckTractor={onUpdateTruckTractor} />);
}
